import React, { lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './index.css';
import reportWebVitals from './reportWebVitals';
import AppLayout from './components/app-layout';
import Login from './pages/login';
import { COLORS } from './theme';
import { hasSession } from './utils/auth';
import { initTheme } from './lib/theme';
import InvoicePage from './pages/transaction/invoice';
import Report from './pages/transaction/report';
import BroadcastNew from './pages/broadcast/new-broadcast';
import RecentBroadcast from './pages/broadcast/recent-broadcast';

initTheme();

// Pages load on demand, so the first visit only downloads the page being opened
const Dashboard = lazy(() => import('./pages/dashobard'));
const TransactionMobileRecharge = lazy(() => import('./pages/transaction/mobile-dth'));
const TransactionBillPayment = lazy(() => import('./pages/transaction/bill-payment'));
const TransactionPending = lazy(() => import('./pages/transaction/pending'));
const TransactionPayment = lazy(() => import('./pages/transaction/payment'));
const TransactionAllPayment = lazy(() => import('./pages/transaction/all-payment'));
const TransactionLedger = lazy(() => import('./pages/transaction/ledger'));
const LiveTransaction = lazy(() => import('./pages/transaction/live'));
const AllUsers = lazy(() => import('./pages/users/all'));
const Tickets = lazy(() => import('./pages/ticket'));
const MarginPlan = lazy(() => import('./pages/margin/plan'));
const Margin = lazy(() => import('./pages/margin/setup'));
const Providers = lazy(() => import('./pages/api/provider'));
const ProviderSetup = lazy(() => import('./pages/api/provider-setup'));
const Balance = lazy(() => import('./pages/api/balance'));
const Operators = lazy(() => import('./pages/setting/operator'));
const Banners = lazy(() => import('./pages/setting/banner'));
const ServiceProvider = lazy(() => import('./pages/setting/service-provider'));
const KhatabookParty = lazy(() => import('./pages/khatabook/party'));
const KhatabookBank = lazy(() => import('./pages/khatabook/bank'));
const KhatabookPayment = lazy(() => import('./pages/khatabook/payment'));
const Notification = lazy(() => import('./pages/notification'));
const Profile = lazy(() => import('./pages/profile'));
const Support = lazy(() => import('./pages/support'));
const Theme = lazy(() => import('./pages/theme'));
const ComingSoon = lazy(() => import('./pages/coming-soon'));

// Signed-in users skip the login screen
const GuestOnly = ({ children }) => (hasSession() ? <Navigate to="/dashboard" replace /> : children);

// Colours come from CSS variables (index.css) so toasts follow light / dark mode
const toastOptions = {
  style: {
    borderRadius: '12px',
    padding: '10px 14px',
    fontSize: '14px',
    background: 'var(--toast-bg)',
    color: 'var(--toast-fg)',
    boxShadow: '0 10px 30px -12px rgb(15 23 42 / 0.25)',
  },
  success: { iconTheme: { primary: COLORS.success, secondary: '#fff' } },
  error: { iconTheme: { primary: COLORS.danger, secondary: '#fff' } },
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <Routes>
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />

      {/* Sidebar + header stay mounted; only the page area below them changes */}
      <Route element={<AppLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/broadcast/new-broadcast" element={<BroadcastNew />} />
        <Route path="/broadcast/recent-broadcast" element={<RecentBroadcast />} />
        <Route path="/transaction/mobile-dth" element={<TransactionMobileRecharge />} />
        <Route path="/transaction/bill-payment" element={<TransactionBillPayment />} />
        <Route path="/transaction/pending" element={<TransactionPending />} />
        <Route path="/transaction/payment" element={<TransactionPayment />} />
        <Route path="/transaction/all-payment" element={<TransactionAllPayment />} />
        <Route path="/transaction/ledger" element={<TransactionLedger />} />
        <Route path="/transaction/live" element={<LiveTransaction />} />
        <Route path="/transaction/invoice" element={<InvoicePage />} />
        <Route path="/transaction/report" element={<Report />} />
        <Route path="/users/all" element={<AllUsers />} />
        <Route path="/ticket" element={<Tickets />} />
        <Route path="/margin/plan" element={<MarginPlan />} />
        <Route path="/margin/setup" element={<Margin />} />
        <Route path="/api/providers" element={<Providers />} />
        <Route path="/api/provider-setup" element={<ProviderSetup />} />
        <Route path="/api/balance" element={<Balance />} />
        <Route path="/setting/operator" element={<Operators />} />
        <Route path="/setting/banner" element={<Banners />} />
        <Route path="/setting/service-provider" element={<ServiceProvider />} />
        <Route path="/khatabook/party" element={<KhatabookParty />} />
        <Route path="/khatabook/bank" element={<KhatabookBank />} />
        <Route path="/khatabook/payment" element={<KhatabookPayment />} />
        <Route path="/notification" element={<Notification />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/support" element={<Support />} />
        <Route path="/theme" element={<Theme />} />

        <Route path="/khatabook/report" element={<ComingSoon title="Khatabook Report" />} />
        <Route path="/analytics/user-balance" element={<ComingSoon title="User Balance" />} />
        <Route path="/analytics/recharge" element={<ComingSoon title="Recharge Analytics" />} />
        <Route path="/analytics/bill-payment" element={<ComingSoon title="Bill Payment Analytics" />} />
        <Route path="/analytics/payment" element={<ComingSoon title="Payment Analytics" />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
    <Toaster position="top-right" toastOptions={toastOptions} />
  </BrowserRouter>
);
reportWebVitals();
